import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import useParticipants from '../../../../hooks/useParticipants'
import { useContentSharing } from '../../../../context/ContentSharingContext'
import { useCourseStudyPlan } from '../../../../hooks/useCourseStudyPlan'
import { useSessionParams } from '../../../../hooks/useSessionParams'
import { ProgressIndicator } from './ProgressIndicator'
import HelpRequestModal from '../../../../components/Modals/HelpRequestModal'
import { useZoomContext } from '../../../../context/ZoomContext'
import { SidebarModeType } from '../../types/types'

type Props = {
    title: string
    handleClick: () => void
    showCloseButton?: boolean
    isContentMode?: boolean
    isExpanded?: boolean
    onToggleExpand?: () => void
    sidebarMode?: SidebarModeType
}

const SidebarHeader = ({
    title,
    handleClick,
    showCloseButton = true,
    isContentMode = false,
    isExpanded = false,
    onToggleExpand,
    sidebarMode,
}: Props) => {
    const { t } = useTranslation('sidebar')
    const { totalUserCount } = useParticipants()
    const { isContentShared, isContentSharer, currentStepId } = useContentSharing()
    const { _classId: courseId } = useSessionParams()
    const { currentSession } = useCourseStudyPlan(courseId)
    const { setConfirmationDialog } = useZoomContext()

    const handleHelpRequest = () => {
        setConfirmationDialog(
            <HelpRequestModal
                isActive={true}
                onClose={() => setConfirmationDialog(undefined)}
                onComplete={() => setConfirmationDialog(undefined)}
            />
        )
    }

    // Get current step index
    const getCurrentStepIndex = () => {
        if (!currentSession || !currentStepId) return 0

        const index = currentSession.steps.findIndex(step => step.id === currentStepId)
        return index !== -1 ? index : 0
    }

    return (
        <SidebarHeaderStyled>
            {isContentMode && onToggleExpand && (
                <ActionButton onClick={onToggleExpand} data-tooltip-id="expand-tooltip" style={{ marginLeft: '5px' }}>
                    <FontAwesomeIcon icon={['fas', isExpanded ? 'compress' : 'expand']} />
                </ActionButton>
            )}
            <div className="title">
                <div>{title}</div>
                <div className="connected">
                    {sidebarMode === 'content' && currentSession ? (
                        <ProgressIndicator
                            currentStepIndex={getCurrentStepIndex()}
                            totalSteps={currentSession.steps.length}
                            tooltipId="sidebar-progress-tooltip"
                        />
                    ) : (
                        t('totalConnected', { count: totalUserCount })
                    )}
                </div>
            </div>
            <div className="actions">
                {showCloseButton && !isContentSharer && (
                    <ActionButton
                        onClick={handleClick}
                        title={isContentMode && isContentShared && !isContentSharer ? t('content.hidePanel') : ''}
                    >
                        <FontAwesomeIcon icon={['fas', 'times']} />
                    </ActionButton>
                )}
                {isContentSharer && (
                    <ActionButton
                        onClick={handleHelpRequest}
                        data-tooltip-id="call-mentor-tooltip"
                        title={t('content.callMentor')}
                    >
                        <FontAwesomeIcon icon={['far', 'user-headset']} />
                    </ActionButton>
                )}
            </div>
        </SidebarHeaderStyled>
    )
}

export default SidebarHeader

const SidebarHeaderStyled = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid ${props => props.theme.color.sidebar.gray1};
    padding: 8px 10px;
    text-transform: uppercase;
    font-weight: bold;
    letter-spacing: 0.05rem;
    font-size: 13px;
    color: white;
    .title {
        flex: 1;
        text-align: center;
        .connected {
            color: ${props => props.theme.color.sidebar.gray3};
            font-size: 11px;
        }
    }
    .actions {
        display: flex;
        gap: 8px;
    }
`

const ActionButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    font-size: 18px;
    color: white;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s ease;

    &:hover {
        color: ${props => props.theme.color.electricBlue};
    }
`
